import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { auth as authApi } from '../utils/api';
import DynamicFormFields from './DynamicFormFields';

const CreateClientModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    password: '', // Auto-generated or set by admin
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (newData) => {
    setFormData(newData);
  };

  const generatePassword = () => {
    // Generate a random 8-character password
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
    toast.info(`Generated password: ${password}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Register the client with all form data
      await authApi.register({
        ...formData,
        firstName: formData.first_name,
        lastName: formData.last_name,
      });

      toast.success(`Client created! Email: ${formData.email}, Password: ${formData.password}`);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Create client error:', error);
      toast.error(error.response?.data?.error || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Create New Client</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Dynamic form fields */}
          <DynamicFormFields
            formData={formData}
            onChange={handleChange}
          />

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password * <span className="text-gray-400 font-normal">(min. 6 characters)</span>
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                minLength={6}
                placeholder="Type a password or click Auto-Generate"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={generatePassword}
                title="Fills the password field with a random password — does not save"
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
              >
                🎲 Auto-Generate
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Make sure to share this password with the client before saving
            </p>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The client will receive their login credentials. 
              They can change their password after logging in.
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClientModal;
